import React, { useState, useEffect } from 'react';
import { X, ExternalLink, BookOpen, Lightbulb, FileText, Target, Award } from 'lucide-react';
import { storage } from '../../services/storage';

interface ResearchApplication {
  source_id: string;
  application_summary: string;
  key_influence: string;
  cited_finding: string;
  applied_to: string;
  context: string;
  source_metadata: {
    title: string;
    authors: string[];
    journal: string;
    year: number;
    doi: string | null;
    key_findings: string[];
    evidence_strength: string;
    consensus_level: number;
  };
}

interface ResearchApplicationModalProps {
  goalId: string;
  goalTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ResearchApplicationModal: React.FC<ResearchApplicationModalProps> = ({
  goalId,
  goalTitle,
  isOpen,
  onClose
}) => {
  const [applications, setApplications] = useState<ResearchApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && goalId) {
      fetchResearchApplications();
    }
  }, [isOpen, goalId]);

  const fetchResearchApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/goals/${goalId}/research-applications`, {
        headers: {
          'Authorization': `Bearer ${await storage.getItem('jwt_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load research applications');
      }

      const data = await response.json();
      setApplications(data.research_applications || []);
    } catch (err) {
      console.error('Error fetching research applications:', err);
      setError('Unable to load research applications');
    } finally {
      setLoading(false);
    }
  };

  const toggleSourceDetails = (compositeKey: string) => {
    setExpandedSources(prev => {
      const newSet = new Set(prev);
      if (newSet.has(compositeKey)) {
        newSet.delete(compositeKey);
      } else {
        newSet.add(compositeKey);
      }
      return newSet;
    });
  };

  const getContextLabel = (context: string) => {
    const labels: Record<string, string> = {
      'goal_synthesis': 'Goal Profile',
      'framework_generation': 'Training Framework',
      'weekly_plan': 'Weekly Plan',
      'workout_card': 'Workout Design'
    };
    return labels[context] || context;
  };

  const getAppliedToLabel = (appliedTo: string) => {
    const labels: Record<string, string> = {
      'goal_briefing': 'Goal Briefing',
      'timeline_assessment': 'Timeline Planning',
      'safety_considerations': 'Safety Protocols',
      'motivation_alignment': 'Motivation Strategy',
      'phase_structure': 'Training Phases',
      'milestone_timing': 'Milestone Planning',
      'adaptation_rules': 'Adaptation Strategy',
      'progression_pattern': 'Progression Logic',
      'volume_distribution': 'Training Volume'
    };
    return labels[appliedTo] || appliedTo;
  };

  const getEvidenceColor = (strength: string) => {
    // 🎨 SPEC COMPLIANCE (JAN 2026): Use CSS variables instead of hardcoded colors
    const colors: Record<string, string> = {
      'strong': 'bg-[var(--cedar-subtle)] text-[var(--cedar-primary)] border border-[var(--cedar-primary)]',
      'very_strong': 'bg-[var(--cedar-primary)] text-white',
      'moderate': 'bg-[var(--cedar-accent-sky)] bg-opacity-10 text-[var(--cedar-accent-sky)] border border-[var(--cedar-accent-sky)]',
      'emerging': 'bg-blue-50 text-blue-700 border border-blue-200',
      'weak': 'bg-gray-100 text-gray-600 border border-gray-300'
    };
    return colors[strength] || 'bg-gray-100 text-gray-600 border border-gray-300';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - 🎨 SPEC COMPLIANCE: Matches FrameworkDetailsModal */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Research-Backed Decisions</h2>
            <p className="text-sm text-gray-600 mt-1">{goalTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - 🎨 SPEC COMPLIANCE: Consistent spacing */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--cedar-primary)]"></div>
            </div>
          ) : error ? (
            <div className="m-6 bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-red-800">{error}</p>
            </div>
          ) : applications.length === 0 ? (
            <div className="m-6 bg-gray-50 rounded-lg p-8 text-center">
              <BookOpen size={48} className="mx-auto mb-4" style={{ color: 'var(--cedar-dark)' }} />
              <p className="text-gray-600">No research applications available yet</p>
              <p className="text-sm text-gray-500 mt-2">
                Research backing will appear once your goal profile is generated
              </p>
            </div>
          ) : (
            <div className="p-6">
              {/* 🎨 SPEC COMPLIANCE: Overview section like FrameworkDetailsModal */}
              <div className="mb-6 p-4 rounded-lg border-2 border-[var(--cedar-primary)] bg-[var(--cedar-subtle)]">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen size={20} className="text-[var(--cedar-primary)]" />
                  <h3 className="font-semibold text-[var(--cedar-dark)]">Research Foundation</h3>
                </div>
                <p className="text-sm text-[var(--cedar-dark)]">
                  Each research source below explains exactly how it informed your personalized training approach.
                </p>
              </div>

              {/* 🎨 SPEC COMPLIANCE: Accordion-style sources matching phase pattern */}
              <div className="space-y-3">
                {applications.map((app, index) => {
                  // 🐛 FIX JAN 21: Use composite key (source_id + context) to prevent multiple items opening
                  // Same research source can appear in multiple contexts (goal_synthesis, framework_generation, etc.)
                  const compositeKey = `${app.source_id}-${app.context}`;
                  const isExpanded = expandedSources.has(compositeKey);
                  
                  return (
                    <div
                      key={compositeKey}
                      className={`border rounded-lg overflow-hidden transition-all ${
                        isExpanded
                          ? 'border-[var(--cedar-primary)] bg-[var(--cedar-subtle)]'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      {/* Source Header - Clickable (matches phase header pattern) */}
                      <button
                        onClick={() => toggleSourceDetails(compositeKey)}
                        className="w-full p-4 flex items-center justify-between hover:bg-opacity-80 transition-colors"
                      >
                        <div className="flex-1 pr-4 text-left">
                          <p className="text-sm font-medium text-gray-900 mb-1 leading-snug">
                            {app.source_metadata.title}
                          </p>
                          <p className="text-xs text-gray-600 mb-1">
                            {app.source_metadata.authors.slice(0, 2).join(', ')}
                            {app.source_metadata.authors.length > 2 && ' et al.'} ({app.source_metadata.year})
                          </p>
                          <p className="text-xs text-gray-500">{app.source_metadata.journal}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEvidenceColor(app.source_metadata.evidence_strength)}`}>
                            {app.source_metadata.evidence_strength}
                          </span>
                          <svg
                            className={`w-5 h-5 text-gray-600 transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                      {/* Expanded Content - 🎨 SPEC COMPLIANCE: Matches phase details pattern */}
                      {isExpanded && (
                        <div className="px-4 pb-4 space-y-4 animate-fadeIn">
                          {/* Context Badge */}
                          <div>
                            <span className="px-3 py-1 bg-white border border-[var(--cedar-primary)] rounded-full text-xs font-medium text-[var(--cedar-primary)]">
                              {getContextLabel(app.context)}
                            </span>
                          </div>

                          {/* Application Summary - PRIMARY CONTENT */}
                          {/* 🎨 CONTRAST FIX JAN 21: White background on green container for better readability */}
                          <div className="bg-white rounded-lg p-4 border border-[var(--cedar-primary)]">
                            <div className="flex items-start gap-3">
                              <Lightbulb size={20} className="text-[var(--cedar-primary)] mt-0.5 flex-shrink-0" />
                              <div>
                                <h4 className="font-semibold text-[var(--cedar-dark)] mb-2">
                                  How This Research Informed Your Plan
                                </h4>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                  {app.application_summary}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Key Details - 🎨 SPEC COMPLIANCE: Vertical stacked like phase sections */}
                          {/* 🎨 CONTRAST FIX JAN 21: Full white background for better contrast */}
                          <div className="space-y-3">
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                                <Target size={16} className="mr-2 text-[var(--cedar-primary)]" />
                                Key Influence
                              </h5>
                              <p className="text-sm text-gray-700">{app.key_influence}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                                <FileText size={16} className="mr-2 text-[var(--cedar-primary)]" />
                                Applied To
                              </h5>
                              <p className="text-sm text-gray-700">{getAppliedToLabel(app.applied_to)}</p>
                            </div>
                          </div>

                          {/* Research Finding Section - 🎨 SPEC COMPLIANCE */}
                          {/* 🎨 CONTRAST FIX JAN 21: Full white background for better contrast */}
                          <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                              <Award size={16} className="mr-2 text-[var(--cedar-primary)]" />
                              Cited Finding
                            </h5>
                            <p className="text-sm italic text-gray-700 mb-4">"{app.cited_finding}"</p>
                            
                            {/* Key Findings */}
                            {app.source_metadata.key_findings && app.source_metadata.key_findings.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <span className="text-xs font-medium text-gray-500 block mb-2">Additional Key Findings</span>
                                <ul className="space-y-2">
                                  {app.source_metadata.key_findings.slice(0, 3).map((finding, idx) => (
                                    <li key={idx} className="text-sm text-gray-700 flex items-start">
                                      <span className="mr-2 text-[var(--cedar-primary)] font-bold">•</span>
                                      <span>{finding}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Read Full Paper Link */}
                            {app.source_metadata.doi && (
                              <div className="mt-4 pt-3 border-t border-gray-200">
                                <a
                                  href={`https://doi.org/${app.source_metadata.doi}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-sm text-[var(--cedar-primary)] hover:text-[var(--cedar-dark)] font-medium transition-colors"
                                >
                                  Read Full Paper
                                  <ExternalLink size={14} />
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer - 🎨 SPEC COMPLIANCE: Matches FrameworkDetailsModal footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <p className="text-xs text-gray-600 text-center">
            All recommendations are backed by {applications.length} peer-reviewed {applications.length === 1 ? 'source' : 'sources'} and tailored to your specific situation
          </p>
        </div>
      </div>
    </div>
  );
};

